import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // App Title
      appTitle: "PetSnap",
      appSubtitle: "Your AI-powered companion for dog and cat care, training, and wellness",

      // Authentication
      signIn: "Sign In",
      signUp: "Sign Up",
      createAccount: "Create Account",
      signInWithGoogle: "Continue with Google",
      signOut: "Sign Out",
      welcome: "Welcome",
      displayName: "Display Name",
      emailAddress: "Email Address",
      password: "Password",
      confirmPassword: "Confirm Password",
      signingIn: "Signing In...",
      creatingAccount: "Creating Account...",
      dontHaveAccount: "Don't have an account? Sign up",
      alreadyHaveAccount: "Already have an account? Sign in",

      // Chat
      chatHeader: "PetSnap",
      chatSubheader: "Your AI companion for pet care guidance",
      typeMessage: "Type your message...",
      helloMessage: "Hello! I'm your PetSnap Assistant",
      helloDescription: "I'm here to help you with dog and cat care, training, health, and wellness. Feel free to ask me anything about your pets!",
      initializingConversation: "Initializing conversation...",

      // Conversation List
      chatHistory: "Chat History",
      newChat: "New Chat",
      newConversation: "New Conversation",
      generalChat: "All Chat",
      planQA: "Plan Q&A",
      memory: "Memory",
      creating: "Creating...",
      searchPlaceholder: "Search conversations or hashtags...",
      searchConversationsOrHashtags: "Search conversations or hashtags...",
      noConversationsFound: "No conversations found matching",
      noConversationsFoundMatching: "No conversations found matching \"{{searchTerm}}\"",
      clearSearch: "Clear search",
      noConversationsYet: "No conversations yet. Start your first chat!",
      exportConversations: "Export Conversations",

      // Conversation Actions
      editTitle: "Edit Title",
      delete: "Delete",
      deleteConversation: "Delete Conversation",
      deleteConfirmation: "Are you sure you want to delete this conversation? This action cannot be undone.",
      areYouSureYouWantToDeleteThisConversation: "Are you sure you want to delete this conversation? This action cannot be undone.",
      cancel: "Cancel",

      // Main Welcome
      welcomeTitle: "Welcome to PetSnap",
      welcomeDescription: "Start a new conversation to get personalized guidance on dog and cat care, training, health, and wellness. Your AI assistant is here to help support your pet's journey.",
      startNewConversation: "Start New Conversation",

      // Landing Page
      landing: {
        title: "Welcome to PetSnap",
        subtitle: "Your AI-powered companion for personalized dog and cat care, training, and wellness guidance",
        listenButton: "Listen to Introduction",
        pauseIntro: "Pause Introduction",
        getStartedButton: "Get Started",
        skipHint: "You can skip the introduction and sign in anytime",
        loadingAudio: "Loading...",
      },

      // Common
      or: "OR",
      save: "Save",
      edit: "Edit",
      unknown: "Unknown",
      unknownBreed: "Unknown",
      unknownActivity: "Unknown",

      // Pet Types
      dog: "Dog",
      cat: "Cat",

      // Plan Q&A
      day: "Day",
      daySuffix: "",
      qaAbout: "Q&A about",
      youAreAskingAbout: "You're asking about",

      showHistory: "SHOW HISTORY",
      hideHistory: "HIDE HISTORY",
      askQuestionAboutPlanElement: "Ask a question about this plan element...",
      tip: "Tip",
      goal: "Goal",
      activity: "Activity",
      parentSharing: "Parent Sharing",
      general: "General",
      conversation: "conversation",
      conversations: "conversations",

      // Error Messages
      errorInitializing: "Error initializing conversation. Please try refreshing the page.",
      conversationNotFound: "Conversation not found",
      errorLoadingMessages: "Error loading messages",
      errorCreatingConversation: "Error creating conversation",
      errorUpdatingTitle: "Error updating conversation title",
      errorDeletingConversation: "Error deleting conversation",
      errorEditingMessage: "Error editing message",
      errorGeneratingRoadmap: "Error generating roadmap. Please try again.",
      errorProcessingResponse: "Error processing response. Please try again.",
      errorGeneratingCompletionReflection: "Failed to generate completion reflection. Please try again.",
      errorThreadNotInitialized: "Unable to generate completion reflection: Thread not initialized",
      errorProcessingReflectionResponse: "Unable to process reflection response: Thread not initialized",
      errorProcessingReflectionResponseRetry: "Failed to process reflection response. Please try again.",
      errorEditingMessageWithDetails: "Error editing message",

      // Auth Errors
      auth: {
        googleError: "Failed to sign in with Google. Please try again.",
        createAccountError: "Failed to create account. Please try again.",
        signInError: "Failed to sign in. Please check your credentials.",
        emailInUse: "An account with this email already exists.",
        invalidEmail: "Please enter a valid email address.",
        weakPassword: "Password is too weak. Please choose a stronger password.",
        userNotFound: "No account found with this email address.",
        wrongPassword: "Incorrect password. Please try again.",
        tooManyRequests: "Too many failed attempts. Please try again later.",
        passwordMismatch: "Passwords do not match",
        passwordTooShort: "Password must be at least 6 characters long",
        fillAllFields: "Please fill in all required fields"
      },

      // Onboarding Module
      onboarding: {
        initializing: "Initializing...",
        retry: "Retry",
        processing: "Processing...",
        title: "PetSnap Onboarding",
        subtitle: "Create your pet's profile",

        steps: {
          welcome: "Welcome",
          photoUpload: "Upload Photo",
          aiAnalysis: "Photo Uploaded",
          confirmDetails: "Confirm Details",
          completed: "Completed"
        },

        // Welcome Step
        welcome: {
          title: "Welcome to PetSnap! 🐾",
          subtitle: "Let's create a personalized profile for your furry friend",
          description: "Build a comprehensive pet profile by uploading a photo and gathering key details. This will enable personalized care recommendations and training plans.",
          features: [
            {
              title: "Pet Photo Upload",
              description: "Upload a photo to personalize your pet's profile"
            },
            {
              title: "Personalized Recommendations",
              description: "Get tailored care advice based on your pet's unique profile"
            },
            {
              title: "Complete Pet Profile",
              description: "Build a comprehensive profile for better pet care and planning"
            }
          ],
          startButton: "Let's Get Started!",
          cancelButton: "Maybe Later"
        },

        // Photo Upload Step
        photoUpload: {
          title: "Let's Start with a Photo 📸",
          description: "Upload a clear photo of your pet to personalize their profile.",
          petTypeLabel: "What type of pet do you have?",
          uploadButton: "Choose Photo",
          analyzeButton: "Upload & Continue",
          cancelButton: "Cancel",
          dragDropText: "Drag and drop a photo here, or click to select",
          fileRequirements: "Supported formats: JPEG, PNG, WebP (max 10MB)",
          selectPetTypeError: "Please select your pet type",
          noFileError: "Please select a photo to upload",
          changePhoto: "Change Photo",
          analyzing: "Uploading..."
        },

        // AI Analysis Step
        aiAnalysis: {
          title: "Photo Upload in Progress 📸",
          analyzing: "Uploading your pet's photo...",
          resultsTitle: "Upload Complete!",
          breedLabel: "Breed",
          ageLabel: "Age",
          sexLabel: "Sex",
          confidenceLabel: "Confidence",
          descriptionLabel: "Description",
          processingMessage: "We are uploading your pet's photo to their profile."
        },

        // Confirm Details Step
        confirmDetails: {
          title: "Review & Confirm Details ✏️",
          description: "Please fill out the intake form to create your pet's complete profile. These details will help care providers understand your pet's needs.",
          petNameLabel: "Pet's Name *",
          breedLabel: "Breed",
          ageLabel: "Age (years)",
          sexLabel: "Sex",
          saveButton: "Create Pet Profile",
          cancelButton: "Go Back",
          nameRequiredError: "Please enter your pet's name",
          creatingProfile: "Creating your pet's profile...",
          unknown: "Unknown"
        },

        // Completion Step
        completion: {
          title: "Welcome to the Family! 🎉",
          congratulations: "Congratulations! Your pet's profile has been created successfully.",
          petName: "Meet",
          profileComplete: "Profile Complete",
          viewProfile: "View Profile",
          addAnotherPet: "Add Another Pet",
          petDetails: "Pet Details",
          breed: "Breed",
          age: "Age",
          sex: "Sex",
          type: "Type",
          nextSteps: "What's Next?",
          nextStepsDesc: "You can now access personalized care recommendations, training plans, and track your pet's progress.",
          exploreFeatures: "Explore Features",
          startExploring: "Start Exploring PetSnap!"
        }
      },
      intakeForm: {
        title: "🐾 Pet Profile & Care Intake Form",
        description: "To ensure your furry friend has a safe, comfortable, and happy stay, please provide the following details.",
        sections: {
          general: "1. General Information",
          health: "2. Health & Nutrition",
          behavior: "3. Behavior & Daily Habits",
          favorites: "4. Favorites & Recreation",
          special: "5. Special Instructions"
        },
        fields: {
          petName: "Pet's Name",
          petType: "Pet Type",
          dog: "Dog",
          cat: "Cat",
          other: "Other",
          breed: "Breed",
          age: "Age",
          gender: "Gender",
          male: "Male",
          female: "Female",
          unknown: "Unknown",
          emergencyContact: "Emergency Contact Number",
          vetContact: "Animal Clinic Center contact phone #",
          foodAllergiesLabel: "Food Allergies:",
          foodAllergiesDesc: "Does your pet have any food allergies or dietary restrictions?",
          yes: "Yes",
          no: "No",
          foodAllergiesDetails: "Please list specific meats, grains, etc.",
          freshTreatLabel: "Fresh Treat Permission:",
          freshTreatDesc: "May we offer your pet fresh carrots, cucumbers, celery, leafy greens, or plain yogurt?",
          medicalStatusLabel: "Medical Status:",
          medicalStatusDesc: "Is your pet spayed or neutered?",
          pottyHabits: "Potty Habits (e.g., uses pads, goes outside, frequency)",
          barkingHabits: "Barking Habits (e.g., barks at strangers, quiet, vocal when playing)",
          boardingDateFrom: "Boarding Date From",
          boardingDateTo: "Boarding Date To",
          chewingHabitsLabel: "Chewing Habits:",
          chewingHabitsDesc: "Does your pet have a tendency to chew on non-toy items?",
          chewingHabitsDetails: "Please explain what they chew on (furniture, shoes, etc.)",
          separationAnxiety: "Separation Anxiety (e.g., distress when you leave, pacing, whining)",
          favoriteThingsLabel: "Favorite Things:",
          favoriteThings: {
            balls: "Balls",
            bones: "Dog Bones/Chews",
            plush: "Plush Toys",
            other: "Other"
          },
          favoriteThingsOtherDetails: "What other things does your pet love?",
          medicationsLabel: "Medications:",
          medicationsDesc: "Does your pet take any medications? If yes, please list the schedule and dosages.",
          walkingPreferences: "Walking Preferences (e.g., pulls on leash, winter walks, traffic)",
          specialNeeds: "Special Instructions",
          specialInstructionsDesc: "(e.g., Preferred sleeping spot, medication schedule, specific commands, or fears like thunder/loud noises)",
          submit: "Submit Profile",
          submitting: "Submitting..."
        },
        route: {
          loading: "Loading Intake Form...",
          invalidLink: "Invalid professional link.",
          success: "Thank you! Your pet's profile has been submitted successfully. The professional will review it shortly.",
          submitFailed: "Failed to submit the form. Please try again later.",
          backToHome: "Return to Homepage"
        }
      }
    }
  },
  es: {
    translation: {
      // App Title
      appTitle: "PetSnap",
      appSubtitle: "Tu compañero impulsado por IA para el cuidado de perros y gatos, entrenamiento y bienestar",

      // Authentication
      signIn: "Iniciar Sesión",
      signUp: "Registrarse",
      createAccount: "Crear Cuenta",
      signInWithGoogle: "Continuar con Google",
      signOut: "Cerrar Sesión",
      welcome: "Bienvenido",
      displayName: "Nombre para Mostrar",
      emailAddress: "Dirección de Correo",
      password: "Contraseña",
      confirmPassword: "Confirmar Contraseña",
      signingIn: "Iniciando Sesión...",
      creatingAccount: "Creando Cuenta...",
      dontHaveAccount: "¿No tienes cuenta? Regístrate",
      alreadyHaveAccount: "¿Ya tienes cuenta? Inicia sesión",

      // Chat
      chatHeader: "PetSnap",
      chatSubheader: "Tu compañero de IA para orientación en el cuidado de mascotas",
      typeMessage: "Escribe tu mensaje...",
      helloMessage: "¡Hola! Soy tu Asistente de PetSnap",
      helloDescription: "Estoy aquí para ayudarte con el cuidado de perros y gatos, entrenamiento, salud y bienestar. ¡Siéntete libre de preguntarme cualquier cosa sobre tus mascotas!",
      initializingConversation: "Inicializando conversación...",

      // Conversation List
      chatHistory: "Historial de Chat",
      newChat: "Nuevo Chat",
      newConversation: "Nueva Conversación",
      generalChat: "Todo el Chat",
      planQA: "Preguntas y Respuestas del Plan",
      memory: "Memoria",
      creating: "Creando...",
      searchPlaceholder: "Buscar conversaciones o hashtags...",
      searchConversationsOrHashtags: "Buscar conversaciones o hashtags...",
      noConversationsFound: "No se encontraron conversaciones que coincidan con",
      noConversationsFoundMatching: "No se encontraron conversaciones que coincidan con \"{{searchTerm}}\"",
      clearSearch: "Limpiar búsqueda",
      noConversationsYet: "Aún no hay conversaciones. ¡Inicia tu primer chat!",
      exportConversations: "Exportar Conversaciones",

      // Conversation Actions
      editTitle: "Editar Título",
      delete: "Eliminar",
      deleteConversation: "Eliminar Conversación",
      deleteConfirmation: "¿Estás seguro de que quieres eliminar esta conversación? Esta acción no se puede deshacer.",
      areYouSureYouWantToDeleteThisConversation: "¿Estás seguro de que quieres eliminar esta conversación? Esta acción no se puede deshacer.",
      cancel: "Cancelar",

      // Main Welcome
      welcomeTitle: "Bienvenido a PetSnap",
      welcomeDescription: "Inicia una nueva conversación para obtener orientación personalizada sobre el cuidado de perros y gatos, entrenamiento, salud y bienestar. Tu asistente de IA está aquí para ayudar a apoyar el viaje de tu mascota.",
      startNewConversation: "Iniciar Nueva Conversación",

      // Landing Page
      landing: {
        title: "Bienvenido a PetSnap",
        subtitle: "Tu compañero impulsado por IA para cuidado personalizado de perros y gatos, entrenamiento y orientación de bienestar",
        listenButton: "Escuchar Introducción",
        pauseIntro: "Pausar Introducción",
        getStartedButton: "Comenzar",
        skipHint: "Puedes omitir la introducción e iniciar sesión en cualquier momento",
        loadingAudio: "Cargando...",
      },

      // Common
      or: "O",
      save: "Guardar",
      edit: "Editar",
      unknown: "Desconocido",
      unknownBreed: "Desconocida",
      unknownActivity: "Desconocida",

      // Pet Types
      dog: "Perro",
      cat: "Gato",

      // Plan Q&A
      day: "Día",
      daySuffix: "",
      qaAbout: "Preguntas y Respuestas sobre",
      youAreAskingAbout: "Estás preguntando sobre",

      showHistory: "MOSTRAR HISTORIAL",
      hideHistory: "OCULTAR HISTORIAL",
      askQuestionAboutPlanElement: "Haz una pregunta sobre este elemento del plan...",
      tip: "Consejo",
      goal: "Objetivo",
      activity: "Actividad",
      parentSharing: "Compartir de Padres",
      general: "General",
      conversation: "conversación",
      conversations: "conversaciones",

      // Error Messages
      errorInitializing: "Error al inicializar la conversación. Por favor, intenta actualizar la página.",
      conversationNotFound: "Conversación no encontrada",
      errorLoadingMessages: "Error al cargar mensajes",
      errorCreatingConversation: "Error al crear conversación",
      errorUpdatingTitle: "Error al actualizar el título de la conversación",
      errorDeletingConversation: "Error al eliminar conversación",
      errorEditingMessage: "Error al editar mensaje",
      errorGeneratingRoadmap: "Error al generar el mapa de ruta. Por favor, inténtalo de nuevo.",
      errorProcessingResponse: "Error al procesar la respuesta. Por favor, inténtalo de nuevo.",
      errorGeneratingCompletionReflection: "Error al generar la reflexión de finalización. Por favor, inténtalo de nuevo.",
      errorThreadNotInitialized: "No se puede generar la reflexión de finalización: Hilo no inicializado",
      errorProcessingReflectionResponse: "No se puede procesar la respuesta de reflexión: Hilo no inicializado",
      errorProcessingReflectionResponseRetry: "Error al procesar la respuesta de reflexión. Por favor, inténtalo de nuevo.",
      errorEditingMessageWithDetails: "Error al editar mensaje",

      // Auth Errors
      auth: {
        googleError: "Error al iniciar sesión con Google. Por favor, inténtalo de nuevo.",
        createAccountError: "Error al crear cuenta. Por favor, inténtalo de nuevo.",
        signInError: "Error al iniciar sesión. Por favor, verifica tus credenciales.",
        emailInUse: "Ya existe una cuenta con este correo electrónico.",
        invalidEmail: "Por favor, ingresa una dirección de correo válida.",
        weakPassword: "La contraseña es muy débil. Por favor, elige una contraseña más fuerte.",
        userNotFound: "No se encontró cuenta con esta dirección de correo.",
        wrongPassword: "Contraseña incorrecta. Por favor, inténtalo de nuevo.",
        tooManyRequests: "Demasiados intentos fallidos. Por favor, inténtalo más tarde.",
        passwordMismatch: "Las contraseñas no coinciden",
        passwordTooShort: "La contraseña debe tener al menos 6 caracteres",
        fillAllFields: "Por favor, completa todos los campos requeridos"
      },

      // Onboarding Module
      onboarding: {
        initializing: "Inicializando...",
        retry: "Reintentar",
        processing: "Procesando...",
        title: "Incorporación a PetSnap",
        subtitle: "Crea el perfil de tu mascota",

        steps: {
          welcome: "Bienvenido",
          photoUpload: "Subir Foto",
          aiAnalysis: "Foto Subida",
          confirmDetails: "Confirmar Detalles",
          completed: "Completado"
        },

        // Welcome Step
        welcome: {
          title: "¡Bienvenido a PetSnap! 🐾",
          subtitle: "Creemos un perfil personalizado para tu amigo peludo",
          description: "Crea un perfil completo para tu mascota subiendo una foto y proporcionando detalles clave. Esto permitirá recomendaciones de cuidado personalizadas y planes de entrenamiento.",
          features: [
            {
              title: "Subir Foto de Mascota",
              description: "Sube una foto para personalizar el perfil de tu mascota"
            },
            {
              title: "Recomendaciones Personalizadas",
              description: "Obtén consejos de cuidado adaptados basados en el perfil único de tu mascota"
            },
            {
              title: "Perfil Completo de Mascota",
              description: "Construye un perfil completo para un mejor cuidado y planificación de mascotas"
            }
          ],
          startButton: "¡Comencemos!",
          cancelButton: "Quizás Después"
        },

        // Photo Upload Step
        photoUpload: {
          title: "Empecemos con una Foto 📸",
          description: "Sube una foto clara de tu mascota para personalizar su perfil.",
          petTypeLabel: "¿Qué tipo de mascota tienes?",
          uploadButton: "Elegir Foto",
          analyzeButton: "Subir y Continuar",
          cancelButton: "Cancelar",
          dragDropText: "Arrastra y suelta una foto aquí, o haz clic para seleccionar",
          fileRequirements: "Formatos admitidos: JPEG, PNG, WebP (máx 10MB)",
          selectPetTypeError: "Por favor selecciona el tipo de tu mascota",
          noFileError: "Por favor selecciona una foto para subir",
          changePhoto: "Cambiar Foto",
          analyzing: "Subiendo..."
        },

        // AI Analysis Step
        aiAnalysis: {
          title: "Subida de Foto en Progreso 📸",
          analyzing: "Subiendo la foto de tu mascota...",
          resultsTitle: "¡Subida Completada!",
          breedLabel: "Raza",
          ageLabel: "Edad",
          sexLabel: "Sexo",
          confidenceLabel: "Confianza",
          descriptionLabel: "Descripción",
          processingMessage: "Estamos subiendo la foto de tu mascota a su perfil."
        },

        // Confirm Details Step
        confirmDetails: {
          title: "Revisar y Confirmar Detalles ✏️",
          description: "Por favor complete el formulario de admisión para crear el perfil completo de su mascota. Estos detalles ayudarán a los cuidadores a entender las necesidades de su mascota.",
          petNameLabel: "Nombre de la Mascota *",
          breedLabel: "Raza",
          ageLabel: "Edad (años)",
          sexLabel: "Sexo",
          saveButton: "Crear Perfil de Mascota",
          cancelButton: "Regresar",
          nameRequiredError: "Por favor ingresa el nombre de tu mascota",
          creatingProfile: "Creando el perfil de tu mascota...",
          unknown: "Desconocido"
        },

        // Completion Step
        completion: {
          title: "¡Bienvenido a la Familia! 🎉",
          congratulations: "¡Felicitaciones! El perfil de tu mascota ha sido creado exitosamente.",
          petName: "Conoce a",
          profileComplete: "Perfil Completado",
          viewProfile: "Ver Perfil",
          addAnotherPet: "Agregar Otra Mascota",
          petDetails: "Detalles de la Mascota",
          breed: "Raza",
          age: "Edad",
          sex: "Sexo",
          type: "Tipo",
          nextSteps: "¿Qué Sigue?",
          nextStepsDesc: "Ahora puedes acceder a recomendaciones de cuidado personalizadas, planes de entrenamiento y rastrear el progreso de tu mascota.",
          exploreFeatures: "Explorar Características",
          startExploring: "¡Comienza a Explorar PetSnap!"
        }
      },
      intakeForm: {
        title: "🐾 Formulario de Ingreso y Cuidado de Mascotas",
        description: "Para asegurar que su amigo peludo tenga una estadía segura, cómoda y feliz, por favor proporcione los siguientes detalles.",
        sections: {
          general: "1. Información General",
          health: "2. Salud y Nutrición",
          behavior: "3. Comportamiento y Hábitos Diarios",
          favorites: "4. Favoritos y Recreación",
          special: "5. Instrucciones Especiales"
        },
        fields: {
          petName: "Nombre de la Mascota",
          petType: "Tipo de Mascota",
          dog: "Perro",
          cat: "Gato",
          other: "Otro",
          breed: "Raza",
          age: "Edad",
          gender: "Género",
          male: "Macho",
          female: "Hembra",
          unknown: "Desconocido",
          emergencyContact: "Número de Contacto de Emergencia",
          vetContact: "Número de clínica veterinaria",
          foodAllergiesLabel: "Alergias Alimentarias:",
          foodAllergiesDesc: "¿Tiene su mascota alguna alergia alimentaria o restricción dietética?",
          yes: "Sí",
          no: "No",
          foodAllergiesDetails: "Por favor enumere carnes específicas, granos, etc.",
          freshTreatLabel: "Permiso de Premios Frescos:",
          freshTreatDesc: "¿Podemos ofrecerle a su mascota zanahorias frescas, pepinos, apio, verduras de hoja o yogur natural?",
          medicalStatusLabel: "Estado Médico:",
          medicalStatusDesc: "¿Está su mascota esterilizada o castrada?",
          pottyHabits: "Hábitos de Baño (ej., usa almohadillas, va afuera, frecuencia)",
          barkingHabits: "Hábitos de Ladrido (ej., ladra a extraños, tranquilo, ruidoso al jugar)",
          boardingDateFrom: "Fecha de Embarque Desde",
          boardingDateTo: "Fecha de Embarque Hasta",
          chewingHabitsLabel: "Hábitos de Masticación:",
          chewingHabitsDesc: "¿Tiene su mascota tendencia a masticar artículos que no sean juguetes?",
          chewingHabitsDetails: "Por favor explique qué mastican (muebles, zapatos, etc.)",
          separationAnxiety: "Ansiedad por Separación (ej., angustia al irse, gemidos, pasear)",
          favoriteThingsLabel: "Cosas Favoritas:",
          favoriteThings: {
            balls: "Pelotas",
            bones: "Huesos para Perros",
            plush: "Juguetes de Peluche",
            other: "Otro"
          },
          favoriteThingsOtherDetails: "¿Qué otras cosas le encantan a tu mascota?",
          medicationsLabel: "Medicamentos:",
          medicationsDesc: "¿Toma su mascota algún medicamento? Si es así, enumere el horario y las dosis.",
          walkingPreferences: "Preferencias de Paseo (ej., tira de la correa, paseos de invierno, tráfico)",
          specialNeeds: "Instrucciones Especiales",
          specialInstructionsDesc: "(ej., Lugar preferido para dormir, horario de medicamentos, comandos específicos o miedos como truenos/ruidos fuertes)",
          submit: "Enviar Perfil",
          submitting: "Enviando..."
        },
        route: {
          loading: "Cargando formulario de ingreso...",
          invalidLink: "Enlace de profesional inválido.",
          success: "¡Gracias! El perfil de su mascota ha sido enviado con éxito. El profesional lo revisará en breve.",
          submitFailed: "Error al enviar el formulario. Por favor, inténtelo de nuevo más tarde.",
          backToHome: "Volver a la Página de Inicio"
        }
      }
    }
  },
  fr: {
    translation: {
      // App Title
      appTitle: "PetSnap",
      appSubtitle: "Votre compagnon alimenté par l'IA pour les soins des chiens et chats, dressage et bien-être",

      // Authentication
      signIn: "Se Connecter",
      signUp: "S'Inscrire",
      createAccount: "Créer un Compte",
      signInWithGoogle: "Continuer avec Google",
      signOut: "Se Déconnecter",
      welcome: "Bienvenue",
      displayName: "Nom d'Affichage",
      emailAddress: "Adresse E-mail",
      password: "Mot de Passe",
      confirmPassword: "Confirmer le Mot de Passe",
      signingIn: "Connexion en cours...",
      creatingAccount: "Création du compte...",
      dontHaveAccount: "Vous n'avez pas de compte ? Inscrivez-vous",
      alreadyHaveAccount: "Vous avez déjà un compte ? Connectez-vous",

      // Chat
      chatHeader: "PetSnap",
      chatSubheader: "Votre compagnon IA pour l'orientation des soins des animaux",
      typeMessage: "Tapez votre message...",
      helloMessage: "Bonjour ! Je suis votre Assistant PetSnap",
      helloDescription: "Je suis là pour vous aider avec les soins des chiens et chats, le dressage, la santé et le bien-être. N'hésitez pas à me poser des questions sur vos animaux !",
      initializingConversation: "Initialisation de la conversation...",

      // Conversation List
      chatHistory: "Historique des Conversations",
      newChat: "Nouveau Chat",
      newConversation: "Nouvelle Conversation",
      generalChat: "Tout le Chat",
      planQA: "Questions et Réponses du Plan",
      memory: "Mémoire",
      creating: "Création...",
      searchPlaceholder: "Rechercher des conversations ou hashtags...",
      searchConversationsOrHashtags: "Rechercher des conversations ou hashtags...",
      noConversationsFound: "Aucune conversation trouvée correspondant à",
      noConversationsFoundMatching: "Aucune conversation trouvée correspondant à \"{{searchTerm}}\"",
      clearSearch: "Effacer la recherche",
      noConversationsYet: "Aucune conversation pour le moment. Commencez votre premier chat !",
      exportConversations: "Exporter les Conversations",

      // Conversation Actions
      editTitle: "Modifier le Titre",
      delete: "Supprimer",
      deleteConversation: "Supprimer la Conversation",
      deleteConfirmation: "Êtes-vous sûr de vouloir supprimer cette conversation ? Cette action ne peut pas être annulée.",
      areYouSureYouWantToDeleteThisConversation: "Êtes-vous sûr de vouloir supprimer cette conversation ? Cette action ne peut pas être annulée.",
      cancel: "Annuler",

      // Main Welcome
      welcomeTitle: "Bienvenue dans PetSnap",
      welcomeDescription: "Commencez une nouvelle conversation pour obtenir des conseils personnalisés sur les soins des chiens et chats, le dressage, la santé et le bien-être. Votre assistant IA est là pour aider à soutenir le parcours de votre animal.",
      startNewConversation: "Commencer une Nouvelle Conversation",

      // Landing Page
      landing: {
        title: "Bienvenue sur PetSnap",
        subtitle: "Votre compagnon alimenté par l'IA pour des soins personnalisés de chiens et chats, dressage et conseils de bien-être",
        listenButton: "Écouter l'Introduction",
        pauseIntro: "Mettre en Pause",
        getStartedButton: "Commencer",
        skipHint: "Vous pouvez ignorer l'introduction et vous connecter à tout moment",
        loadingAudio: "Chargement...",
      },

      // Common
      or: "OU",
      save: "Enregistrer",
      edit: "Modifier",
      unknown: "Inconnu",
      unknownBreed: "Inconnue",
      unknownActivity: "Inconnue",

      // Pet Types
      dog: "Chien",
      cat: "Chat",

      // Plan Q&A
      day: "Jour",
      daySuffix: "",
      qaAbout: "Questions et Réponses sur",
      youAreAskingAbout: "Vous demandez à propos de",

      showHistory: "AFFICHER L'HISTORIQUE",
      hideHistory: "MASQUER L'HISTORIQUE",
      askQuestionAboutPlanElement: "Posez une question sur cet élément du plan...",
      tip: "Conseil",
      goal: "Objectif",
      activity: "Activité",
      parentSharing: "Partage Parental",
      general: "Général",
      conversation: "conversation",
      conversations: "conversations",

      // Error Messages
      errorInitializing: "Erreur lors de l'initialisation de la conversation. Veuillez actualiser la page.",
      conversationNotFound: "Conversation non trouvée",
      errorLoadingMessages: "Erreur lors du chargement des messages",
      errorCreatingConversation: "Erreur lors de la création de la conversation",
      errorUpdatingTitle: "Erreur lors de la mise à jour du titre de la conversation",
      errorDeletingConversation: "Erreur lors de la suppression de la conversation",
      errorEditingMessage: "Erreur lors de la modification du message",
      errorGeneratingRoadmap: "Erreur lors de la génération du plan de route. Veuillez réessayer.",
      errorProcessingResponse: "Erreur lors du traitement de la réponse. Veuillez réessayer.",
      errorGeneratingCompletionReflection: "Échec de la génération de la réflexion de finalisation. Veuillez réessayer.",
      errorThreadNotInitialized: "Impossible de générer la réflexion de finalisation : Fil non initialisé",
      errorProcessingReflectionResponse: "Impossible de traiter la réponse de réflexion : Fil non initialisé",
      errorProcessingReflectionResponseRetry: "Échec du traitement de la réponse de réflexion. Veuillez réessayer.",
      errorEditingMessageWithDetails: "Erreur lors de la modification du message",

      // Auth Errors
      auth: {
        googleError: "Échec de la connexion avec Google. Veuillez réessayer.",
        createAccountError: "Échec de la création du compte. Veuillez réessayer.",
        signInError: "Échec de la connexion. Veuillez vérifier vos identifiants.",
        emailInUse: "Un compte avec cet e-mail existe déjà.",
        invalidEmail: "Veuillez entrer une adresse e-mail valide.",
        weakPassword: "Le mot de passe est trop faible. Veuillez choisir un mot de passe plus fort.",
        userNotFound: "Aucun compte trouvé avec cette adresse e-mail.",
        wrongPassword: "Mot de passe incorrect. Veuillez réessayer.",
        tooManyRequests: "Trop de tentatives échouées. Veuillez réessayer plus tard.",
        passwordMismatch: "Les mots de passe ne correspondent pas",
        passwordTooShort: "Le mot de passe doit contenir au moins 6 caractères",
        fillAllFields: "Veuillez remplir tous les champs requis"
      },

      // Onboarding Module
      onboarding: {
        initializing: "Initialisation...",
        retry: "Réessayer",
        processing: "Traitement...",
        title: "Intégration PetSnap",
        subtitle: "Créez le profil de votre animal",

        steps: {
          welcome: "Bienvenue",
          photoUpload: "Télécharger Photo",
          aiAnalysis: "Photo Téléchargée",
          confirmDetails: "Confirmer Détails",
          completed: "Terminé"
        },

        // Welcome Step
        welcome: {
          title: "Bienvenue sur PetSnap ! 🐾",
          subtitle: "Créons un profil personnalisé pour votre ami à fourrure",
          description: "Créez un profil complet pour votre animal en téléchargeant une photo et en fournissant des détails clés. Cela permettra des recommandations de soins personnalisées et des plans d'entraînement.",
          features: [
            {
              title: "Télécharger la Photo",
              description: "Téléchargez une photo pour personnaliser le profil de votre animal"
            },
            {
              title: "Recommandations Personnalisées",
              description: "Obtenez des conseils de soins adaptés basés sur le profil unique de votre animal"
            },
            {
              title: "Profil Complet d'Animal",
              description: "Construisez un profil complet pour de meilleurs soins et une meilleure planification"
            }
          ],
          startButton: "Commençons !",
          cancelButton: "Peut-être Plus Tard"
        },

        // Photo Upload Step
        photoUpload: {
          title: "Commençons par une Photo 📸",
          description: "Téléchargez une photo claire de votre animal pour personnaliser son profil.",
          petTypeLabel: "Quel type d'animal avez-vous ?",
          uploadButton: "Choisir Photo",
          analyzeButton: "Télécharger et Continuer",
          cancelButton: "Annuler",
          dragDropText: "Glissez-déposez une photo ici, ou cliquez pour sélectionner",
          fileRequirements: "Formats pris en charge : JPEG, PNG, WebP (max 10MB)",
          selectPetTypeError: "Veuillez sélectionner le type de votre animal",
          noFileError: "Veuillez sélectionner une photo à télécharger",
          changePhoto: "Changer Photo",
          analyzing: "Téléchargement..."
        },

        // AI Analysis Step
        aiAnalysis: {
          title: "Téléchargement de Photo en Cours 📸",
          analyzing: "Téléchargement de la photo de votre animal...",
          resultsTitle: "Téléchargement Terminé !",
          breedLabel: "Race",
          ageLabel: "Âge",
          sexLabel: "Sexe",
          confidenceLabel: "Confiance",
          descriptionLabel: "Description",
          processingMessage: "Nous téléchargeons la photo de votre animal sur son profil."
        },

        // Confirm Details Step
        confirmDetails: {
          title: "Vérifier et Confirmer les Détails ✏️",
          description: "Veuillez remplir le formulaire d'admission pour créer le profil complet de votre animal. Ces détails aideront les soignants à comprendre ses besoins.",
          petNameLabel: "Nom de l'Animal *",
          breedLabel: "Race",
          ageLabel: "Âge (ans)",
          sexLabel: "Sexe",
          saveButton: "Créer le Profil de l'Animal",
          cancelButton: "Retour",
          nameRequiredError: "Veuillez saisir le nom de votre animal",
          creatingProfile: "Création du profil de votre animal...",
          unknown: "Inconnu"
        },

        // Completion Step
        completion: {
          title: "Bienvenue dans la Famille ! 🎉",
          congratulations: "Félicitations ! Le profil de votre animal a été créé avec succès.",
          petName: "Rencontrez",
          profileComplete: "Profil Terminé",
          viewProfile: "Voir le Profil",
          addAnotherPet: "Ajouter un Autre Animal",
          petDetails: "Détails de l'Animal",
          breed: "Race",
          age: "Âge",
          sex: "Sexe",
          type: "Type",
          nextSteps: "Quelle est la Prochaine Étape ?",
          nextStepsDesc: "Vous pouvez maintenant accéder à des recommandations de soins personnalisées, des plans d'entraînement et suivre les progrès de votre animal.",
          exploreFeatures: "Explorer les Fonctionnalités",
          startExploring: "Commencez à Explorer PetSnap !"
        }
      },
      intakeForm: {
        title: "🐾 Formulaire d'Admission et de Soins pour Animaux",
        description: "Pour s'assurer que votre ami à fourrure passe un séjour sûr, confortable et heureux, veuillez fournir les détails suivants.",
        sections: {
          general: "1. Informations Générales",
          health: "2. Santé et Nutrition",
          behavior: "3. Comportement et Habitudes Quotidiennes",
          favorites: "4. Favoris et Récréation",
          special: "5. Instructions Spéciales"
        },
        fields: {
          petName: "Nom de l'animal",
          petType: "Type d'animal",
          dog: "Chien",
          cat: "Chat",
          other: "Autre",
          breed: "Race",
          age: "Âge",
          gender: "Sexe",
          male: "Mâle",
          female: "Femelle",
          unknown: "Inconnu",
          emergencyContact: "Numéro de contact d'urgence",
          vetContact: "Numéro de clinique vétérinaire",
          foodAllergiesLabel: "Allergies Alimentaires:",
          foodAllergiesDesc: "Votre animal a-t-il des allergies alimentaires ou des restrictions diététiques ?",
          yes: "Oui",
          no: "Non",
          foodAllergiesDetails: "Veuillez lister les viandes spécifiques, céréales, etc.",
          freshTreatLabel: "Permission de Friandises Fraîches:",
          freshTreatDesc: "Pouvons-nous offrir à votre animal des carottes fraîches, des concombres, du céleri, des légumes-feuilles ou du yaourt nature ?",
          medicalStatusLabel: "État Médical:",
          medicalStatusDesc: "Votre animal est-il stérilisé ou castré ?",
          pottyHabits: "Habitudes de Propreté (ex: utilise des alèses, va dehors, fréquence)",
          barkingHabits: "Habitudes d'Aboiement (ex: aboie sur les étrangers, calme, vocal en jouant)",
          boardingDateFrom: "Date d'embarquement à partir de",
          boardingDateTo: "Date d'embarquement jusqu'à",
          chewingHabitsLabel: "Habitudes de Mastication :",
          chewingHabitsDesc: "Votre animal a-t-il tendance à mâcher des articles autres que des jouets ?",
          chewingHabitsDetails: "Veuillez expliquer ce qu'ils mâchent (meubles, chaussures, etc.)",
          separationAnxiety: "Anxiété de Séparation (ex: détresse lors de votre départ, gémissements, allées et venues)",
          favoriteThingsLabel: "Choses Préférées :",
          favoriteThings: {
            balls: "Balles",
            bones: "Os pour Chiens",
            plush: "Jouets en Peluche",
            other: "Autre"
          },
          favoriteThingsOtherDetails: "Quelles autres choses votre animal aime-t-il ?",
          medicationsLabel: "Médicaments :",
          medicationsDesc: "Votre animal prend-il des médicaments ? Si oui, veuillez lister l'horaire et les doses.",
          walkingPreferences: "Préférences de Marche (ex: tire sur la laisse, promenades d'hiver, trafic)",
          specialNeeds: "Instructions Spéciales",
          specialInstructionsDesc: "(ex: Place de sommeil préférée, horaire des médicaments, commandes spécifiques ou peurs comme le tonnerre/bruits forts)",
          submit: "Soumettre Profil",
          submitting: "Soumission en cours..."
        },
        route: {
          loading: "Chargement du formulaire d'admission...",
          invalidLink: "Lien professionnel invalide.",
          success: "Merci ! Le profil de votre animal a été soumis avec succès. Le professionnel l'examinera sous peu.",
          submitFailed: "Échec de la soumission du formulaire. Veuillez réessayer plus tard.",
          backToHome: "Retour à la Page d'Accueil"
        }
      }
    }
  },
  'zh-CN': {
    translation: {
      // App Title
      appTitle: "PetSnap",
      appSubtitle: "您的AI驱动伴侣，专注于狗狗和猫咪的护理、训练和健康",

      // Authentication
      signIn: "登录",
      signUp: "注册",
      createAccount: "创建账户",
      signInWithGoogle: "使用Google继续",
      signOut: "退出登录",
      welcome: "欢迎",
      displayName: "显示名称",
      emailAddress: "电子邮箱地址",
      password: "密码",
      confirmPassword: "确认密码",
      signingIn: "登录中...",
      creatingAccount: "创建账户中...",
      dontHaveAccount: "没有账户？立即注册",
      alreadyHaveAccount: "已有账户？立即登录",

      // Chat
      chatHeader: "PetSnap",
      chatSubheader: "您的AI宠物护理指导伴侣",
      typeMessage: "输入您的消息...",
      helloMessage: "您好！我是您的PetSnap助手",
      helloDescription: "我在这里帮助您了解狗狗和猫咪的护理、训练、健康和福利。请随时向我提问关于您的宠物的问题！",
      initializingConversation: "初始化对话中...",

      // Conversation List
      chatHistory: "聊天记录",
      newChat: "新建聊天",
      newConversation: "新对话",
      generalChat: "全部聊天",
      planQA: "计划问答",
      memory: "记忆",
      creating: "创建中...",
      searchPlaceholder: "搜索对话或标签...",
      searchConversationsOrHashtags: "搜索对话或标签...",
      noConversationsFound: "未找到匹配的对话",
      noConversationsFoundMatching: "未找到匹配\"{{searchTerm}}\"的对话",
      clearSearch: "清除搜索",
      noConversationsYet: "暂无对话。开始您的第一次聊天吧！",
      exportConversations: "导出对话",

      // Conversation Actions
      editTitle: "编辑标题",
      delete: "删除",
      deleteConversation: "删除对话",
      deleteConfirmation: "您确定要删除此对话吗？此操作无法撤销。",
      areYouSureYouWantToDeleteThisConversation: "您确定要删除此对话吗？此操作无法撤销。",
      cancel: "取消",

      // Main Welcome
      welcomeTitle: "欢迎使用PetSnap",
      welcomeDescription: "开始新对话，获取关于狗狗和猫咪护理、训练、健康和福利的个性化指导。您的AI助手在这里帮助支持您的宠物成长之旅。",
      startNewConversation: "开始新对话",

      // Landing Page
      landing: {
        title: "欢迎使用PetSnap",
        subtitle: "您的AI智能伴侣，为狗狗和猫咪提供个性化护理、训练和健康指导",
        listenButton: "收听介绍",
        pauseIntro: "暂停介绍",
        getStartedButton: "开始使用",
        skipHint: "您可以跳过介绍，随时登录",
        loadingAudio: "加载中...",
      },

      // Common
      or: "或",
      save: "保存",
      edit: "编辑",
      unknown: "未知",
      unknownBreed: "未知",
      unknownActivity: "未知",

      // Pet Types
      dog: "狗",
      cat: "猫",

      // Plan Q&A
      day: "第",
      daySuffix: "天",
      qaAbout: "问答关于",
      youAreAskingAbout: "您正在询问关于",

      showHistory: "显示历史",
      hideHistory: "隐藏历史",
      askQuestionAboutPlanElement: "询问有关此计划元素的问题...",
      tip: "提示",
      goal: "目标",
      activity: "活动",
      parentSharing: "家长分享",
      general: "一般",
      conversation: "对话",
      conversations: "条对话",

      // Error Messages
      errorInitializing: "初始化对话时出错。请尝试刷新页面。",
      conversationNotFound: "未找到对话",
      errorLoadingMessages: "加载消息时出错",
      errorCreatingConversation: "创建对话时出错",
      errorUpdatingTitle: "更新对话标题时出错",
      errorDeletingConversation: "删除对话时出错",
      errorEditingMessage: "编辑消息时出错",
      errorGeneratingRoadmap: "生成路线图时出错。请重试。",
      errorProcessingResponse: "处理响应时出错。请重试。",
      errorGeneratingCompletionReflection: "生成完成反思失败。请重试。",
      errorThreadNotInitialized: "无法生成完成反思：线程未初始化",
      errorProcessingReflectionResponse: "无法处理反思响应：线程未初始化",
      errorProcessingReflectionResponseRetry: "处理反思响应失败。请重试。",
      errorEditingMessageWithDetails: "编辑消息时出错",

      // Auth Errors
      auth: {
        googleError: "Google登录失败。请重试。",
        createAccountError: "创建账户失败。请重试。",
        signInError: "登录失败。请检查您的凭据。",
        emailInUse: "此邮箱已存在账户。",
        invalidEmail: "请输入有效的邮箱地址。",
        weakPassword: "密码太弱。请选择更强的密码。",
        userNotFound: "未找到此邮箱地址的账户。",
        wrongPassword: "密码错误。请重试。",
        tooManyRequests: "失败尝试次数过多。请稍后重试。",
        passwordMismatch: "密码不匹配",
        passwordTooShort: "密码必须至少6个字符",
        fillAllFields: "请填写所有必填字段"
      },

      // Onboarding Module
      onboarding: {
        initializing: "初始化中...",
        retry: "重试",
        processing: "处理中...",
        title: "PetSnap引导",
        subtitle: "创建您的宠物资料",

        steps: {
          welcome: "欢迎",
          photoUpload: "上传照片",
          aiAnalysis: "照片已上传",
          confirmDetails: "确认详情",
          completed: "完成"
        },

        // Welcome Step
        welcome: {
          title: "欢迎使用PetSnap！🐾",
          subtitle: "让我们为您的毛茸茸朋友创建一个个性化资料",
          description: "通过上传照片并提供关键详情来为您的宠物创建全面的资料。这将实现个性化的护理建议和训练计划。",
          features: [
            {
              title: "上传宠物照片",
              description: "上传照片以个性化您宠物的资料"
            },
            {
              title: "个性化建议",
              description: "根据您宠物的独特资料获得量身定制的护理建议"
            },
            {
              title: "完整的宠物资料",
              description: "构建全面的资料以获得更好的宠物护理和规划"
            }
          ],
          startButton: "让我们开始吧！",
          cancelButton: "以后再说"
        },

        // Photo Upload Step
        photoUpload: {
          title: "让我们从一张照片开始 📸",
          description: "上传您宠物的清晰照片以个性化他们的资料。",
          petTypeLabel: "您有什么类型的宠物？",
          uploadButton: "选择照片",
          analyzeButton: "上传并继续",
          cancelButton: "取消",
          dragDropText: "拖拽照片到这里，或点击选择",
          fileRequirements: "支持格式：JPEG、PNG、WebP（最大10MB）",
          selectPetTypeError: "请选择您的宠物类型",
          noFileError: "请选择要上传的照片",
          changePhoto: "更换照片",
          analyzing: "上传中..."
        },

        // AI Analysis Step
        aiAnalysis: {
          title: "照片上传进行中 📸",
          analyzing: "正在上传您宠物的照片...",
          resultsTitle: "上传完成！",
          breedLabel: "品种",
          ageLabel: "年龄",
          sexLabel: "性别",
          confidenceLabel: "置信度",
          descriptionLabel: "描述",
          processingMessage: "我们正在将您宠物的照片上传到他们的资料中。"
        },

        // Confirm Details Step
        confirmDetails: {
          title: "检查并确认详细信息 ✏️",
          description: "请填写登记表以创建您的宠物完整档案。这些细节将帮助护理人员了解您宠物的需求。",
          petNameLabel: "宠物名字 *",
          breedLabel: "品种",
          ageLabel: "年龄（岁）",
          sexLabel: "性别",
          saveButton: "创建宠物资料",
          cancelButton: "返回",
          nameRequiredError: "请输入您宠物的名字",
          creatingProfile: "正在创建您的宠物资料...",
          unknown: "未知"
        },

        // Completion Step
        completion: {
          title: "欢迎加入家庭！🎉",
          congratulations: "恭喜！您的宠物资料已成功创建。",
          petName: "认识",
          profileComplete: "资料完成",
          viewProfile: "查看资料",
          addAnotherPet: "添加另一只宠物",
          petDetails: "宠物详细信息",
          breed: "品种",
          age: "年龄",
          sex: "性别",
          type: "类型",
          nextSteps: "下一步是什么？",
          nextStepsDesc: "您现在可以访问个性化的护理建议、训练计划，并跟踪宠物的进度。",
          exploreFeatures: "探索功能",
          startExploring: "开始探索PetSnap！"
        }
      },
      intakeForm: {
        title: "🐾 宠物档案与护理接收表",
        description: "为了确保您的毛茸茸的朋友度过一个安全、舒适、快乐的时光，请提供以下详细信息。",
        sections: {
          general: "1. 基本信息",
          health: "2. 健康与营养",
          behavior: "3. 行为与日常习惯",
          favorites: "4. 最爱与娱乐",
          special: "5. 特别说明"
        },
        fields: {
          petName: "宠物名字",
          petType: "宠物类型",
          dog: "狗",
          cat: "猫",
          other: "其他",
          breed: "品种",
          age: "年龄",
          gender: "性别",
          male: "雄性",
          female: "雌性",
          unknown: "未知",
          emergencyContact: "紧急联系电话",
          vetContact: "动物诊所联系电话",
          foodAllergiesLabel: "食物过敏：",
          foodAllergiesDesc: "您的宠物是否有任何食物过敏或饮食限制？",
          yes: "是",
          no: "否",
          foodAllergiesDetails: "请列出具体的肉类、谷物等",
          freshTreatLabel: "生鲜零食许可：",
          freshTreatDesc: "我们可以给您的宠物提供新鲜胡萝卜、黄瓜、芹菜、绿叶蔬菜或原味酸奶吗？",
          medicalStatusLabel: "医疗状况：",
          medicalStatusDesc: "您的宠物已经绝育了吗？",
          pottyHabits: "排便习惯（例如：使用尿垫、去外面、频率）",
          barkingHabits: "吠叫习惯（例如：对陌生人吠叫、安静、玩耍时爱叫）",
          boardingDateFrom: "入住开始日期",
          boardingDateTo: "入住结束日期",
          chewingHabitsLabel: "咬物习惯:",
          chewingHabitsDesc: "您的宠物有咬非玩具物品的倾向吗？",
          chewingHabitsDetails: "请说明它们咬什么（家具、鞋子等）",
          separationAnxiety: "分离焦虑（例如：您离开时会感到焦虑、来回踱步、呜咽）",
          favoriteThingsLabel: "最爱的东西:",
          favoriteThings: {
            balls: "球类",
            bones: "狗骨头/除臭饼干",
            plush: "毛绒玩具",
            other: "其他"
          },
          favoriteThingsOtherDetails: "您的宠物还喜欢其他什么东西？",
          medicationsLabel: "药物：",
          medicationsDesc: "您的宠物是否服用任何药物？如果是，请列出时间表和剂量。",
          walkingPreferences: "散步偏好（例如：拉扯牵引绳、习惯冬天散步、害怕交通）",
          specialNeeds: "特别说明",
          specialInstructionsDesc: "（例如：偏好的睡觉地点、药物时间表、特定指令，或害怕雷声/巨大声响）",
          submit: "提交资料",
          submitting: "提交中..."
        },
        route: {
          loading: "加载接收表单中...",
          invalidLink: "无效的专业人士链接。",
          success: "谢谢！您的宠物档案已成功提交。专业人士将很快进行审核。",
          submitFailed: "表单提交失败，请稍后再试。",
          backToHome: "返回首页"
        }
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: false,

    interpolation: {
      escapeValue: false,
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
  });

export default i18n; 